import React, { useId, useState, type ReactNode } from 'react';
import styled from '@emotion/styled';

export type ClinicalTabItem = {
  key: string;
  label: ReactNode;
  children: ReactNode;
  focusablePanel?: boolean;
};

type Props = {
  items: ClinicalTabItem[];
  defaultActiveKey?: string;
};

const TabList = styled.div`
  display: flex;
  margin-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  [role='tab'] {
    position: relative;
    padding: 12px 1em;
    border: 0;
    background: none;
    font: inherit;
    color: inherit;
    cursor: pointer;

    &[aria-selected='true']::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -1px;
      height: 2px;
      background: var(--productCoral, var(--productPrimary));
    }
  }
`;

const ClinicalTabs = ({ items, defaultActiveKey = items[0]?.key }: Props) => {
  const id = useId();
  const [activeKey, setActiveKey] = useState(defaultActiveKey);

  const [visitedKeys, setVisitedKeys] = useState(() => new Set([defaultActiveKey]));

  const activate = (key: string) => {
    setActiveKey(key);
    setVisitedKeys((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  };

  return (
    <div>
      <TabList role="tablist">
        {items.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            data-testid={`tab-${key}`}
            id={`${id}-tab-${key}`}
            aria-controls={`${id}-panel-${key}`}
            aria-selected={key === activeKey}
            onClick={() => activate(key)}
          >
            {label}
          </button>
        ))}
      </TabList>
      {items.map(({ key, children, focusablePanel }) => (
        <div
          key={key}
          role="tabpanel"
          id={`${id}-panel-${key}`}
          aria-labelledby={`${id}-tab-${key}`}
          tabIndex={focusablePanel ? 0 : undefined}
          hidden={key !== activeKey}
        >
          {visitedKeys.has(key) && children}
        </div>
      ))}
    </div>
  );
};

export default ClinicalTabs;
